import _ from 'lodash';
import Papa from 'papaparse';

/**
 * Download a recordset as a verbatim CSV (raw rows, one column per field).
 *
 * Shared by the per-tool-execution download icon in the status bar. Mirrors the
 * Table renderer's own export so the file is identical to the rendered table's
 * download — and, for pivot/schedule sources, exports the underlying raw rows
 * rather than the presented layout.
 *
 * @param {Object} recordset
 * @param {Array} recordset.data - Array of row objects
 * @param {Array} recordset.fields - Field descriptors ({ label, exportLabel, path, enumValues })
 * @param {string} [recordset.filename] - Base filename (without extension)
 */
export default function downloadRecordsetCsv({ data, fields, filename }) {
  if (!Array.isArray(data) || !Array.isArray(fields)) return;

  const rows = data.map((row) =>
    Object.fromEntries(
      fields.map((f) => {
        const header = f.exportLabel ?? f.label;
        // key fallback mirrors the Table renderer: stored authored tables that omitted path are flat
        // and keyed by the model's own field keys.
        const raw = _.get(row, f.path ?? f.key);
        if (raw == null) return [header, ''];
        if (f.enumValues?.[raw]) return [header, f.enumValues[raw]];
        if (Array.isArray(raw))
          return [header, raw.map((v) => (typeof v === 'object' ? (v?.name ?? '') : v)).join(', ')];
        if (typeof raw === 'object') return [header, raw.name ?? raw.title ?? raw.label ?? ''];
        return [header, raw];
      }),
    ),
  );

  const csv = Papa.unparse(rows, { columns: fields.map((f) => f.exportLabel ?? f.label) });
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename || 'table'}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}
